import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {makeStyles, useTheme} from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {lerPercentualSalvo, limitarPercentual, PASSO_TECLADO, percentualDoPonteiro} from './painelDividido.js';

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        width: '100%',
        minHeight: 0
    },
    empilhado: {
        flexDirection: 'column',
        gap: theme.spacing(3)
    },
    // Durante o arrasto o iframe do PDF não pode capturar o mouse, senão o arrasto trava.
    arrastando: {
        userSelect: 'none',
        cursor: 'col-resize',
        '& iframe': {
            pointerEvents: 'none'
        }
    },
    painel: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        minHeight: 0
    },
    direita: {
        flex: 1
    },
    alca: {
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 12,
        margin: theme.spacing(0, 0.5),
        cursor: 'col-resize',
        touchAction: 'none',
        outline: 'none',
        borderRadius: theme.shape.borderRadius,
        '&::before': {
            content: '""',
            width: 4,
            height: 48,
            borderRadius: 2,
            backgroundColor: theme.palette.secondary.main,
            transition: theme.transitions.create(['background-color', 'height'])
        },
        '&:hover::before, &:focus::before, &$alcaAtiva::before': {
            height: 96,
            backgroundColor: theme.palette.primary.main
        }
    },
    alcaAtiva: {}
}));

const lerSalvo = (chave, padrao) => {
    if (!chave) {
        return padrao;
    }

    try {
        return lerPercentualSalvo(window.localStorage.getItem(chave), padrao);
    } catch (e) {
        return padrao;
    }
};

// Preferência só deste navegador: se o armazenamento falhar, a tela segue com a largura em memória.
const gravar = (chave, valor) => {
    if (!chave) {
        return;
    }

    try {
        window.localStorage.setItem(chave, String(valor));
    } catch (e) {
        // sem armazenamento disponível
    }
};

/*
 * Dois painéis lado a lado com uma divisória que o usuário arrasta para ajustar
 * a largura (setas do teclado também ajustam; duplo clique volta ao padrão).
 * A largura fica salva no navegador pela `chaveArmazenamento`. Abaixo de `md`
 * os painéis ficam empilhados, sem divisória.
 */
export default function PainelDividido(props) {
    const {
        esquerda,
        direita,
        chaveArmazenamento,
        percentualInicial = 33,
        minimoEsquerda = 240,
        minimoDireita = 400,
        className
    } = props;
    const classes = useStyles();
    const theme = useTheme();
    const ladoALado = useMediaQuery(theme.breakpoints.up('md'), {noSsr: true});
    const containerRef = useRef(null);
    const [percentual, setPercentual] = useState(() => lerSalvo(chaveArmazenamento, percentualInicial));
    const [largura, setLargura] = useState(0);
    const [arrastando, setArrastando] = useState(false);
    const percentualRef = useRef(percentual);
    const limites = {minimoEsquerda, minimoDireita};

    useEffect(() => {
        if (!ladoALado) {
            return undefined;
        }

        const medir = () => containerRef.current && setLargura(containerRef.current.getBoundingClientRect().width);
        medir();
        window.addEventListener('resize', medir);
        return () => window.removeEventListener('resize', medir);
    }, [ladoALado]);

    const efetivo = limitarPercentual(percentual, largura, limites);
    percentualRef.current = efetivo;

    const aplicar = (valor, salvar) => {
        setPercentual(valor);
        if (salvar) {
            gravar(chaveArmazenamento, valor);
        }
    };

    const aoPressionar = event => {
        if (event.button !== 0) {
            return;
        }

        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        setArrastando(true);
    };

    const aoMover = event => {
        if (arrastando && containerRef.current) {
            aplicar(percentualDoPonteiro(event.clientX, containerRef.current.getBoundingClientRect(), limites), false);
        }
    };

    const aoSoltar = event => {
        if (!arrastando) {
            return;
        }

        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        setArrastando(false);
        gravar(chaveArmazenamento, percentualRef.current);
    };

    const aoTeclar = event => {
        const passo = {ArrowLeft: -PASSO_TECLADO, ArrowRight: PASSO_TECLADO}[event.key];
        if (!passo) {
            return;
        }

        event.preventDefault();
        aplicar(limitarPercentual(efetivo + passo, largura, limites), true);
    };

    if (!ladoALado) {
        return (
            <div className={clsx(classes.root, classes.empilhado, className)}>
                <div className={classes.painel}>{esquerda}</div>
                <div className={classes.painel}>{direita}</div>
            </div>
        );
    }

    return (
        <div ref={containerRef} className={clsx(classes.root, arrastando && classes.arrastando, className)}>
            {/* A largura muda a cada movimento do ponteiro: vai no style para não recriar a classe JSS. */}
            <div className={classes.painel} style={{flex: `0 0 ${efetivo}%`}}>
                {esquerda}
            </div>
            <div
                role='separator'
                aria-orientation='vertical'
                aria-label='Redimensionar painéis'
                aria-valuenow={Math.round(efetivo)}
                aria-valuemin={Math.round(limitarPercentual(0, largura, limites))}
                aria-valuemax={Math.round(limitarPercentual(100, largura, limites))}
                tabIndex={0}
                title='Arraste para ajustar a largura (duplo clique volta ao padrão)'
                className={clsx(classes.alca, arrastando && classes.alcaAtiva)}
                onPointerDown={aoPressionar}
                onPointerMove={aoMover}
                onPointerUp={aoSoltar}
                onPointerCancel={aoSoltar}
                onKeyDown={aoTeclar}
                onDoubleClick={() => aplicar(percentualInicial, true)}/>
            <div className={clsx(classes.painel, classes.direita)}>
                {direita}
            </div>
        </div>
    );
}

PainelDividido.propTypes = {
    esquerda: PropTypes.node.isRequired,
    direita: PropTypes.node.isRequired,
    /* Chave do localStorage onde a largura escolhida fica salva (sem chave, não salva). */
    chaveArmazenamento: PropTypes.string,
    /* Largura inicial do painel da esquerda, em % do contêiner. */
    percentualInicial: PropTypes.number,
    /* Larguras mínimas, em px, de cada painel. */
    minimoEsquerda: PropTypes.number,
    minimoDireita: PropTypes.number,
    className: PropTypes.string
};
